// frontend/src/components/Dashboard/TrafficSources.jsx
import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faGlobe,
  faSearch,
  faEnvelope,
  faMobileAlt,
  faShoppingCart,
  faUsers,
  faComments,
  faLink,
  faHashtag,
  // faWhatsapp,
  faShip,
  faBuilding,
} from "@fortawesome/free-solid-svg-icons";
import {
  faFacebook,
  faTwitter,
  faLinkedin,
  faInstagram,
  faYoutube,
  faWhatsapp,
  faTiktok,
} from "@fortawesome/free-brands-svg-icons";

// ============================================
// SUPPLIER CONSTANTS
// ============================================
const SUPPLIER = {
  name: "Mylasha",
  country: "Malaysia",
};

const TrafficSources = ({ sources, userRole, title = "Traffic Sources" }) => {
  const iconMap = {
    direct: faGlobe,
    organic: faSearch,
    social: faFacebook,
    facebook: faFacebook,
    twitter: faTwitter,
    linkedin: faLinkedin,
    instagram: faInstagram,
    whatsapp: faWhatsapp,
    youtube: faYoutube,
    tiktok: faTiktok,
    referral: faLink,
    email: faEnvelope,
    mobile: faMobileAlt,
    web: faShoppingCart,
    users: faUsers,
    chat: faComments,
    hashtag: faHashtag,
    malaysia: faShip,
    supplier: faBuilding,
  };

  const colorMap = {
    direct: "#10b981",
    organic: "#3b82f6",
    social: "#8b5cf6",
    facebook: "#1877f2",
    twitter: "#1da1f2",
    linkedin: "#0a66c2",
    instagram: "#e4405f",
    whatsapp: "#25d366",
    youtube: "#ff0000",
    tiktok: "#000000",
    referral: "#f59e0b",
    email: "#ef4444",
    mobile: "#ec4899",
    web: "#14b8a6",
    users: "#6366f1",
    chat: "#06b6d4",
    hashtag: "#8b5cf6",
    malaysia: "#006B2E",
    supplier: "#D9A321",
  };

  const getDefaultSources = () => {
    if (userRole === "farmer") {
      return [
        {
          name: "direct",
          label: "Direct Visit",
          percentage: 40,
          count: 567,
          color: "#10b981",
        },
        {
          name: "whatsapp",
          label: "WhatsApp",
          percentage: 28,
          count: 398,
          color: "#25d366",
        },
        {
          name: "referral",
          label: "Farmer Referral",
          percentage: 20,
          count: 284,
          color: "#f59e0b",
        },
        {
          name: "organic",
          label: "Organic Search",
          percentage: 12,
          count: 170,
          color: "#3b82f6",
        },
        {
          name: "malaysia",
          label: `${SUPPLIER.country} Direct`,
          percentage: 5,
          count: 71,
          color: "#006B2E",
        },
      ];
    }
    if (userRole === "admin") {
      return [
        {
          name: "direct",
          label: "Direct",
          percentage: 30,
          count: 1245,
          color: "#10b981",
        },
        {
          name: "organic",
          label: "Organic Search",
          percentage: 25,
          count: 1038,
          color: "#3b82f6",
        },
        {
          name: "social",
          label: "Social Media",
          percentage: 20,
          count: 830,
          color: "#8b5cf6",
        },
        {
          name: "referral",
          label: "Referral",
          percentage: 10,
          count: 415,
          color: "#f59e0b",
        },
        {
          name: "email",
          label: "Email",
          percentage: 8,
          count: 332,
          color: "#ef4444",
        },
        {
          name: "malaysia",
          label: `${SUPPLIER.country} Origin`,
          percentage: 7,
          count: 290,
          color: "#006B2E",
        },
      ];
    }
    return [
      {
        name: "direct",
        label: "Direct",
        percentage: 35,
        count: 234,
        color: "#10b981",
      },
      {
        name: "organic",
        label: "Organic Search",
        percentage: 28,
        count: 187,
        color: "#3b82f6",
      },
      {
        name: "social",
        label: "Social Media",
        percentage: 20,
        count: 134,
        color: "#8b5cf6",
      },
      {
        name: "referral",
        label: "Referral",
        percentage: 10,
        count: 67,
        color: "#f59e0b",
      },
      {
        name: "malaysia",
        label: `${SUPPLIER.country} Source`,
        percentage: 7,
        count: 47,
        color: "#006B2E",
      },
    ];
  };

  const data = sources && sources.length > 0 ? sources : getDefaultSources();

  const getIcon = (name) => iconMap[name] || iconMap.direct || faGlobe;
  const getColor = (name) => colorMap[name] || "#6b7280";

  return (
    <div className="traffic-sources">
      <div className="traffic-header">
        <h3>
          <FontAwesomeIcon icon={faGlobe} /> {title}
        </h3>
        <span className="traffic-total-badge">
          <FontAwesomeIcon icon={faShip} /> {SUPPLIER.country} Sourced
        </span>
      </div>
      <div className="sources-list">
        {data.map((source, index) => {
          const icon = getIcon(source.name);
          const color = source.color || getColor(source.name);
          const percentage = source.percentage || 0;
          const count = source.count || 0;
          const isMalaysia =
            source.name === "malaysia" ||
            source.label?.includes(SUPPLIER.country);

          return (
            <div
              key={index}
              className={`source-item ${isMalaysia ? "malaysia-source" : ""}`}
            >
              <div className="source-info">
                <div
                  className="source-icon-wrapper"
                  style={{ backgroundColor: `${color}15` }}
                >
                  <FontAwesomeIcon
                    icon={icon}
                    className="source-icon"
                    style={{ color }}
                  />
                </div>
                <span className="source-name">
                  {source.label}
                  {isMalaysia && <span className="malaysia-tag">🇲🇾</span>}
                </span>
              </div>
              <div className="source-stats">
                <span className="source-percentage" style={{ color }}>
                  {percentage}%
                </span>
                <div className="source-bar">
                  <div
                    className="source-bar-fill"
                    style={{
                      width: `${Math.min(percentage, 100)}%`,
                      backgroundColor: color,
                    }}
                  />
                </div>
                <span className="source-count">{count.toLocaleString()}</span>
              </div>
            </div>
          );
        })}
      </div>

      <style>{`
        .traffic-sources {
          background: #ffffff;
          padding: 1.25rem 1.5rem;
          border-radius: 8px;
          border: 1px solid #e5e5e5;
          box-shadow: 0 1px 3px rgba(0,0,0,0.04);
        }

        .traffic-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          flex-wrap: wrap;
          gap: 0.5rem;
          margin-bottom: 1rem;
        }

        .traffic-header h3 {
          color: #006b2e;
          margin: 0;
          font-size: 1rem;
          font-family: "Montserrat", sans-serif;
        }

        .traffic-header h3 svg { color: #d9a321; margin-right: 0.5rem; }

        .traffic-total-badge {
          font-size: 0.65rem;
          padding: 0.2rem 0.75rem;
          border-radius: 12px;
          background: #dcfce7;
          color: #16a34a;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .sources-list {
          display: flex;
          flex-direction: column;
          gap: 0.75rem;
        }

        .source-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 1rem;
          padding: 0.3rem 0.5rem;
          border-radius: 6px;
          transition: all 0.3s ease;
        }

        .source-item:hover { background: #f8f9fa; }

        .source-item.malaysia-source {
          border-left: 3px solid #006B2E;
          background: rgba(0,107,46,0.03);
          padding-left: 0.75rem;
        }

        .source-info {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          min-width: 120px;
        }

        .source-icon-wrapper {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .source-icon { font-size: 0.7rem; }
        .source-name { font-size: 0.75rem; color: #1f1f1f; display: flex; align-items: center; gap: 0.25rem; }
        .malaysia-tag { font-size: 0.7rem; }

        .source-stats {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          flex: 1;
        }

        .source-percentage {
          font-weight: 600;
          font-size: 0.7rem;
          min-width: 45px;
        }

        .source-bar {
          flex: 1;
          height: 4px;
          background: #e5e5e5;
          border-radius: 2px;
          overflow: hidden;
        }

        .source-bar-fill {
          height: 100%;
          border-radius: 2px;
          transition: width 0.6s ease;
        }

        .source-count {
          font-size: 0.6rem;
          color: #9ca3af;
          min-width: 40px;
          text-align: right;
        }

        @media (max-width: 768px) {
          .traffic-sources { padding: 1rem; }
          .source-item { flex-wrap: wrap; gap: 0.5rem; }
          .source-info { min-width: 80px; }
          .source-stats { flex: 1; min-width: 150px; }
          .source-count { min-width: 30px; }
        }

        @media (max-width: 480px) {
          .source-item { flex-direction: column; align-items: stretch; padding: 0.5rem; }
          .source-stats { flex-wrap: wrap; }
          .source-bar { flex: 1; min-width: 60px; }
          .traffic-header { flex-direction: column; align-items: flex-start; }
        }
      `}</style>
    </div>
  );
};

export default TrafficSources;
